import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TourForm } from "@/components/TourForm";
import { TourResults } from "@/components/TourResults";
import { SupportDialog } from "@/components/SupportDialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SEOHead } from "@/components/SEOHead";
import { TourFormData, TourResult } from "@/types/tour";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Beer, MapPin, Route, PartyPopper, Users, Briefcase, Plane } from "lucide-react";
import { cities } from "@/data/cities";
const heroImage = "/hero-1536.jpg";
const heroImageWebp = "/hero-1536.webp";
const heroImageWebpSm = "/hero-768.webp";
const heroImageJpgSm = "/hero-768.jpg";

const Index = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialCity = searchParams.get("city") ?? undefined;
  const [results, setResults] = useState<TourResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const inFlightRef = useRef(false);

  const handleGenerateRoutes = async (formData: TourFormData) => {
    // Guard against double submits (each request is billed by Google)
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-tour", {
        body: { ...formData, language: (i18n.resolvedLanguage || i18n.language || "de").split("-")[0] },
      });

      if (error) throw error;
      setResults(data as TourResult);
      setSupportDialogOpen(true);
      toast.success(t("index.toast_success"));
    } catch (err: any) {
      console.error("Error generating tour:", err);
      toast.error(t("index.toast_error"));
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  const features = [
    { icon: MapPin, title: t("features.anywhere_title"), desc: t("features.anywhere_desc") },
    { icon: Route, title: t("features.routes_title"), desc: t("features.routes_desc") },
    { icon: Beer, title: t("features.schedule_title"), desc: t("features.schedule_desc") },
  ];

  const useCases = [
    { icon: PartyPopper, title: t("seo.use_cases_birthday"), desc: t("seo.use_cases_birthday_desc") },
    { icon: Users, title: t("seo.use_cases_stag"), desc: t("seo.use_cases_stag_desc") },
    { icon: Briefcase, title: t("seo.use_cases_team"), desc: t("seo.use_cases_team_desc") },
    { icon: Plane, title: t("seo.use_cases_travel"), desc: t("seo.use_cases_travel_desc") },
  ];

  const faqs = [
    { q: t("seo.faq_q1"), a: t("seo.faq_a1") },
    { q: t("seo.faq_q2"), a: t("seo.faq_a2") },
    { q: t("seo.faq_q3"), a: t("seo.faq_a3") },
    { q: t("seo.faq_q4"), a: t("seo.faq_a4") },
    { q: t("seo.faq_q5"), a: t("seo.faq_a5") },
    { q: t("seo.faq_q6"), a: t("seo.faq_a6") },
    { q: t("seo.faq_q7"), a: t("seo.faq_a7") },
    { q: t("seo.faq_q8"), a: t("seo.faq_a8") },
  ];

  const seoTitle = t("seo.meta_title");
  const seoDescription = t("seo.meta_description");
  const canonical = "https://goldenmile.bar/";

  const schemaJson = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Golden Mile",
      description: seoDescription,
      applicationCategory: "TravelApplication",
      operatingSystem: "Web",
      url: canonical,
      "@id": `${canonical}#app`,
      isPartOf: { "@id": `${canonical}#website` },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
        schemaJson={schemaJson}
      />
      <SupportDialog open={supportDialogOpen} onOpenChange={setSupportDialogOpen} />
      {/* Hero / Header */}
      <header className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5" />
        <div className="absolute right-3 top-3 z-10 sm:right-4 sm:top-4">
          <LanguageSwitcher />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 py-12 text-center sm:py-16">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
            <Beer className="h-4 w-4" aria-hidden="true" />
            {t("hero.badge")}
          </div>
          <h1 className="mb-3 text-4xl font-bold tracking-tight sm:text-5xl">
            {t("hero.title_part1")} <span className="text-primary">{t("hero.title_highlight")}</span>
          </h1>
          <p className="mx-auto max-w-xl text-muted-foreground">
            {t("hero.subtitle")}
          </p>
          <picture>
            <source
              type="image/webp"
              srcSet={`${heroImageWebpSm} 768w, ${heroImageWebp} 1536w`}
              sizes="(max-width: 768px) 100vw, 768px"
            />
            <source
              type="image/jpeg"
              srcSet={`${heroImageJpgSm} 768w, ${heroImage} 1536w`}
              sizes="(max-width: 768px) 100vw, 768px"
            />
            <img
              src={heroImage}
              alt={t("seo.hero_image_alt")}
              width={1536}
              height={864}
              fetchPriority="high"
              decoding="async"
              className="mx-auto mt-8 w-full max-w-3xl rounded-2xl border border-border/60 shadow-lg"
            />
          </picture>
        </div>
      </header>

      <main id="planner" className="mx-auto max-w-5xl px-4 py-8">
        {!results ? (
          <div className="mx-auto max-w-xl">
            <TourForm onSubmit={handleGenerateRoutes} loading={loading} initialCity={initialCity} />
          </div>
        ) : (
          <div>
            <button
              onClick={() => setResults(null)}
              className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t("index.new_tour")}
            </button>
            <TourResults results={results} />
          </div>
        )}
      </main>

      {/* Features when no results */}
      {!results && !loading && (
        <>
          <section aria-labelledby="features-heading" className="border-t border-border bg-card/50 px-4 py-16">
            <div className="mx-auto max-w-4xl">
              <h2 id="features-heading" className="mb-10 text-center text-2xl font-bold sm:text-3xl">
                {t("seo.about_title")}
              </h2>
              <div className="mx-auto mb-12 max-w-3xl space-y-4 text-center text-muted-foreground">
                <p>{t("seo.about_p1")}</p>
                <p>{t("seo.about_p2")}</p>
              </div>
              <div className="grid gap-8 sm:grid-cols-3">
                {features.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <h3 className="mb-1 font-semibold">{title}</h3>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="how" aria-labelledby="how-heading" className="border-t border-border px-4 py-16">
            <div className="mx-auto max-w-4xl">
              <h2 id="how-heading" className="mb-10 text-center text-2xl font-bold sm:text-3xl">
                {t("seo.how_title")}
              </h2>
              <div className="grid gap-8 sm:grid-cols-3">
                <article>
                  <h3 className="mb-2 font-semibold text-primary">{t("seo.how_step1_title")}</h3>
                  <p className="text-sm text-muted-foreground">{t("seo.how_step1_desc")}</p>
                </article>
                <article>
                  <h3 className="mb-2 font-semibold text-primary">{t("seo.how_step2_title")}</h3>
                  <p className="text-sm text-muted-foreground">{t("seo.how_step2_desc")}</p>
                </article>
                <article>
                  <h3 className="mb-2 font-semibold text-primary">{t("seo.how_step3_title")}</h3>
                  <p className="text-sm text-muted-foreground">{t("seo.how_step3_desc")}</p>
                </article>
              </div>
              <div className="mt-10 text-center">
                <a
                  href="#planner"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  {t("seo.footer_link_planner")} →
                </a>
              </div>
            </div>
          </section>

          <section id="use-cases" aria-labelledby="usecases-heading" className="border-t border-border bg-card/50 px-4 py-16">
            <div className="mx-auto max-w-4xl">
              <h2 id="usecases-heading" className="mb-10 text-center text-2xl font-bold sm:text-3xl">
                {t("seo.use_cases_title")}
              </h2>
              <div className="grid gap-6 sm:grid-cols-2">
                {useCases.map(({ icon: Icon, title, desc }) => (
                  <article key={title} className="rounded-xl border border-border/60 bg-background p-5">
                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <h3 className="mb-1 font-semibold">{title}</h3>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="cities" aria-labelledby="cities-heading" className="border-t border-border px-4 py-16">
            <div className="mx-auto max-w-4xl">
              <h2 id="cities-heading" className="mb-3 text-center text-2xl font-bold sm:text-3xl">
                {t("seo.cities_title")}
              </h2>
              <p className="mx-auto mb-10 max-w-2xl text-center text-muted-foreground">
                {t("seo.cities_description")}
              </p>
              <ul className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                {cities.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/pub-crawl/${c.slug}`}
                      className="flex items-center justify-between rounded-xl border border-border/60 bg-card/50 px-4 py-3 text-sm transition-colors hover:border-primary/40 hover:bg-card"
                    >
                      <span className="font-medium">Pub crawl {c.name}</span>
                      <span className="text-xs text-muted-foreground">{c.country}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section id="faq" aria-labelledby="faq-heading" className="border-t border-border px-4 py-16">
            <div className="mx-auto max-w-3xl">
              <h2 id="faq-heading" className="mb-10 text-center text-2xl font-bold sm:text-3xl">
                {t("seo.faq_title")}
              </h2>
              <div className="space-y-6">
                {faqs.map((f) => (
                  <article key={f.q}>
                    <h3 className="mb-2 font-semibold">{f.q}</h3>
                    <p className="text-muted-foreground">{f.a}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      <footer className="border-t border-border bg-card/30 px-4 py-10">
        <div className="mx-auto grid max-w-5xl gap-8 sm:grid-cols-3">
          <div>
            <p className="mb-2 text-lg font-bold">
              {t("hero.title_part1")} <span className="text-primary">{t("hero.title_highlight")}</span>
            </p>
            <p className="text-sm text-muted-foreground">{t("seo.footer_tagline")}</p>
          </div>
          <nav aria-label="Footer">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("seo.footer_explore")}
            </h2>
            <ul className="space-y-2 text-sm">
              <li><a href="#planner" className="hover:text-primary">{t("seo.footer_link_planner")}</a></li>
              <li><a href="#how" className="hover:text-primary">{t("seo.footer_link_how")}</a></li>
              <li><a href="#use-cases" className="hover:text-primary">{t("seo.footer_link_use_cases")}</a></li>
              <li><a href="#faq" className="hover:text-primary">{t("seo.footer_link_faq")}</a></li>
            </ul>
          </nav>
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t("seo.footer_support")}
            </h2>
            <a
              href="https://buymeacoffee.com/smiethi"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/20"
            >
              <Beer className="h-4 w-4" aria-hidden="true" />
              {t("seo.footer_coffee")}
            </a>
          </div>
        </div>
        <p className="mx-auto mt-8 max-w-5xl text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Golden Mile. {t("seo.footer_rights")}
        </p>
      </footer>
    </div>
  );
};

export default Index;
